import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- add useLocation
import Header from './Header';
import arenaImg from './images/arena.jpeg';
import deleteIcon from './images/delete.png';
import copyIcon from './images/copy.png';
import Calendar from './Calendar';
import './LessonSchedule.css';
import {useAuth} from './hooks/useAuth';
import { authHeader } from "./helpers/auth-header";
import { BS_BASE } from "./configs/api";


const initialAvailability = [
  { day: 'Sun', slots: [] },
  { day: 'Mon', slots: [] },
  { day: 'Tue', slots: [] },
  { day: 'Wed', slots: [] },
  { day: 'Thu', slots: [] },
  { day: 'Fri', slots: [] },
  { day: 'Sat', slots: [] },
];

// Helper to generate 30-min interval times from 7:00am to 9:00pm
function generateTimes() {
  const times = [];
  let hour = 7;
  let min = 0;
  while (hour < 21 || (hour === 21 && min === 0)) {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour < 12 ? 'am' : 'pm';
    times.push(`${h}:${min.toString().padStart(2, '0')}${ampm}`);
    min += 15;
    if (min === 60) {
      min = 0;
      hour += 1;
    }
  }
  return times;
}
const TIME_OPTIONS = generateTimes();

// Helper to generate duration options from 15 min to 2 hours in 15 min intervals
function generateDurationOptions() {
  const options = [];
  for (let min = 15; min <= 120; min += 15) {
    const hours = Math.floor(min / 60);
    const mins = min % 60;
    let label = '';
    if (hours > 0) {
      label += `${hours} hour${hours > 1 ? 's' : ''}`;
      if (mins > 0) label += ` ${mins} minutes`;
    } else {
      label = `${mins} minutes`;
    }
    options.push({ value: `${min} minutes`, label });
  }
  return options;
}
const DURATION_OPTIONS = generateDurationOptions();

function LessonSchedule() {
  const [duration, setDuration] = useState('45 minutes');
  const [availability, setAvailability] = useState(initialAvailability);
  const [dropdowns, setDropdowns] = useState({});
  const [copySourceDay, setCopySourceDay] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showCalendar, setShowCalendar] = useState('from');
  const [pastedDays, setPastedDays] = useState([]);
  const [tab, setTab] = useState(0);
  const isoKey = React.useCallback(
    (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : ''),
    []
  );
  const [scheduledDates, setScheduledDates] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation(); // <-- get location
  const auth = useAuth();
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fromButtonRef = React.useRef(null);
  const toInputRef = React.useRef(null);

  React.useEffect(() => {
    fromButtonRef.current?.focus();
  }, []);

  // Check if school mode is enabled
  const isSchool = location.state && location.state.school === true;

  // Tab labels based on school mode
  const tabLabels = isSchool
    ? ['Slot Details', 'School Availability', 'Confirm']
    : ['Lesson Details', 'Instructor Availability', 'Confirm'];

  // Handlers
  const handleLogout = () => navigate('/admin-dashboard');
  const handleDropdown = (day) => setDropdowns(prev => ({
    ...prev,
    [day]: { open: !prev[day]?.open, start: '', end: '' }
  }));
  const handleTimeChange = (day, field, value) => setDropdowns(prev => ({
    ...prev,
    [day]: { ...prev[day], [field]: value }
  }));
  const handleAddSlot = (day) => {
    const { start, end } = dropdowns[day] || {};
    if (!start || !end) return;
    setAvailability(prev =>
      prev.map(d =>
        d.day === day
          ? { ...d, slots: [...d.slots, { start, end }] }
          : d
      )
    );
    setDropdowns(prev => ({
      ...prev,
      [day]: { open: false, start: '', end: '' }
    }));
  };
  const handleCopySlots = (sourceDay) => setCopySourceDay(sourceDay);
  const handleCopyDone = () => {
    setCopySourceDay(null);
    setPastedDays([]);
  };
  const handlePasteSlots = (targetDay) => {
    if (!copySourceDay) return;
    setAvailability(prev =>
      prev.map(d =>
        d.day === targetDay
          ? { ...d, slots: [...prev.find(day => day.day === copySourceDay).slots] }
          : d
      )
    );
    setPastedDays(prev => [...prev, targetDay]);
  };
  const handleCalendarSelect = (date) => {
    if (showCalendar === 'from') {
      setFromDate(date);
      // After picking From date, automatically focus To date input
      setTimeout(() => {
        toInputRef.current?.focus();
      }, 0);
    } else if (showCalendar === 'to') {
      setToDate(date);
    }
    setShowCalendar(null);
  };
  const formatDate = (date) =>
    date && date instanceof Date
      ? date.toLocaleDateString()
      : typeof date === 'string'
      ? date
      : '';

  // Helper to check if at least one slot is selected
  const hasAnySlots = availability.some(day => day.slots.length > 0);

  // Helper to check if all required fields are filled
  const canConfirm =
    duration &&
    fromDate &&
    toDate &&
    hasAnySlots;

  // Tab navigation
  const nextTab = () => setTab(t => Math.min(t + 1, 2));
  const prevTab = () => setTab(t => Math.max(t - 1, 0));

const handleConfirm = async () => {
  if (!canConfirm) return;
    // Convert duration string like "1 hour 45 minutes" or "45 minutes" to total minutes
    function parseDurationToMinutes(str) {
      const hourMatch = str.match(/(\d+)\s*hour/);
      const minMatch = str.match(/(\d+)\s*minute/);
      const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
      const minutes = minMatch ? parseInt(minMatch[1], 10) : 0;
      return hours * 60 + minutes;
    }

    // Helper to format date as dd/mm/yyyy
    function formatToDDMMYYYY(date) {
      if (date instanceof Date) {
      const d = date.getDate().toString().padStart(2, '0');
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      const y = date.getFullYear();
      return `${d}/${m}/${y}`;
      }
      // If already string, try to parse and format
      const parsed = new Date(date);
      if (!isNaN(parsed)) {
      const d = parsed.getDate().toString().padStart(2, '0');
      const m = (parsed.getMonth() + 1).toString().padStart(2, '0');
      const y = parsed.getFullYear();
      return `${d}/${m}/${y}`;
      }
      return date;
    }

    const payload = {
      isSchool,
      duration: parseDurationToMinutes(duration), // always number of minutes
      fromDate: formatToDDMMYYYY(fromDate),
      toDate: formatToDDMMYYYY(toDate),
      availability,
    };

    setSubmitting(true);
    setStatus(null);
    auth.setSchedule({payload})
      ?.then(() => {
        navigate('/admin-dashboard');
      })
      .catch((error) => {
        setSubmitting(false);
        setStatus(error instanceof Error ? error.message : error);
      });


    /*try {
      const response = await fetch('/api/lesson-schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        alert('Schedule confirmed!');
      } else {
        alert('Failed to confirm schedule.');
      }
    } catch (err) {
      alert('Error confirming schedule.');
    }*/
  };

  // Helper: normalize Date to yyyy-mm-dd
  const isoDay = d => d.toISOString().slice(0, 10);

  // Pull all days in the visible month via slots-by-date
  const fetchMonthScheduled = React.useCallback(
    async (monthDate) => {
      if (isSchool || !(monthDate instanceof Date)) return;

      const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      const daysInMonth = [];
      for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
        daysInMonth.push(new Date(d));
      }

      const results = await Promise.all(
        daysInMonth.map(async (day) => {
          const ddmmyyyy = `${String(day.getDate()).padStart(2, '0')}/${String(day.getMonth() + 1).padStart(2, '0')}/${day.getFullYear()}`;
          try {
            const res = await fetch(
              `${BS_BASE}/slots-by-date?date=${encodeURIComponent(ddmmyyyy)}&isSchool=false`,
              { headers: { Accept: 'application/json', ...authHeader() } }
            );
            if (!res.ok) return null;
            const json = await res.json();
            const list = Array.isArray(json) ? json : json?.slots ?? [];
            return list.length > 0 ? isoKey(day) : null;
          } catch {
            return null;
          }
        })
      );

      setScheduledDates((prev) => {
        const next = new Set(prev);
        results.filter(Boolean).forEach((key) => next.add(key));
        return next;
      });
    },
    [isSchool, isoKey]
  );

  React.useEffect(() => {
    if (!isSchool) fetchMonthScheduled(new Date());
  }, [isSchool, fetchMonthScheduled]);

  const getDayClassName = React.useCallback(
    (date) => {
      const baseClass = !isSchool && scheduledDates.has(isoKey(date)) ? 'calendar-day--scheduled' : '';

      const start = fromDate instanceof Date ? fromDate : fromDate ? new Date(fromDate) : null;
      const end = toDate instanceof Date ? toDate : toDate ? new Date(toDate) : null;

      if (start && end) {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (d >= s && d <= e) {
          return [baseClass, 'calendar-day--in-range'].filter(Boolean).join(' ');
        }
      }

      return baseClass;
    },
    [isSchool, scheduledDates, isoKey, fromDate, toDate]
  );

  return (
    <div className="bookingform-page" style={{ minHeight: "100vh" }}>
      <div className="bookingform-form main-container">
        <Header onBack={handleLogout} />
        <div
          className="admin-dashboard-title"
          style={{
            margin: -5,
            fontSize: "1.2rem",
            fontWeight: 700,
            letterSpacing: 1
          }}
        >
          {isSchool ? "SCHOOL SCHEDULE" : "LESSON SCHEDULE"}
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #ccc", marginBottom: 18 }}>
          {tabLabels.map((label, idx) => (
            <button
              key={label}
              onClick={() => setTab(idx)}
              style={{
                flex: 1,
                padding: "10px 0",
                background: tab === idx ? "#282c34" : "#fff",
                border: "none",
                borderBottom: tab === idx ? "3px solid #4fa3c7" : "3px solid transparent",
                fontWeight: tab === idx ? 700 : 500,
                color: tab === idx ? "#ffffffff" : "#6f90d1ff",
                cursor: "pointer",
                fontSize: "0.95rem"
              }}
              className="tab-label-btn"
            >
              {label}
            </button>
          ))}
        </div>
        {/* Tab 1: Lesson Details */}
        {tab === 0 && (
          <>
            <div style={{ margin: 0, padding: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  margin: -15,
                  padding: 0,
                  minHeight: 0,
                  height: 32 // keeps row height compact
                }}
              >
                <span className="admin-dashboard-label" style={{ textAlign: "center", margin: 0, padding: 0, lineHeight: 1.1 }}>
                  Duration
                </span>
                <select
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  className="admin-dashboard-select"
                  style={{ minWidth: 60, width: 80, maxWidth: 120, margin: 0, height: 24 }}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <hr style={{ width: "80%", border: 0,  borderTop: "1px solid #ccc", margin: "18px auto 2px auto" }} />
            <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: -2, marginBottom: 0 }}>
              <span className="admin-dashboard-label dates-label" style={{ textAlign: "center" }}>
                Dates
              </span>
            </div>
            <div style={{ display: "flex", gap: "18px", margin: "6px 0 0 0", alignItems: "center" }}>
              <label style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", fontSize: "0.95rem" }}>
                From:
                <input
                  type="text"
                  value={formatDate(fromDate)}
                  readOnly
                  placeholder="Select date"
                  style={{
                    marginTop: 4,
                    minWidth: 110,
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "1rem",
                    cursor: "pointer",
                    background: "#fff"
                  }}
                  onClick={() => setShowCalendar('from')}
                  onFocus={() => setShowCalendar('from')}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", fontSize: "0.95rem" }}>
                To:
                <input
                  type="text"
                  value={formatDate(toDate)}
                  readOnly
                  placeholder="Select date"
                  ref={toInputRef}
                  style={{
                    marginTop: 4,
                    minWidth: 110,
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "1rem",
                    cursor: "pointer",
                    background: "#fff"
                  }}
                  onClick={() => setShowCalendar('to')}
                  onFocus={() => setShowCalendar('to')}
                />
              </label>
            </div>
            {/*
            {showCalendar && (
              <div style={{ margin: "0 auto 18px auto", maxWidth: 400 }}>
                <Calendar
                  onDateSelect={handleCalendarSelect}
                  minDate={
                    showCalendar === 'from'
                      ? new Date(new Date().setHours(0,0,0,0))
                      : fromDate && fromDate instanceof Date
                        ? new Date(fromDate.setHours(0,0,0,0))
                        : undefined
                  }
                />
              </div>
            )}
            */}
            <div
              style={{
                margin: "0 auto 18px auto",
                maxWidth: 400,
                opacity: showCalendar ? 1 : 0.5,
                filter: showCalendar ? "none" : "grayscale(100%)",
                pointerEvents: showCalendar ? "auto" : "none",
                transition: "opacity 150ms ease"
              }}
            >
              <Calendar
                onDateSelect={handleCalendarSelect}
                onMonthChange={fetchMonthScheduled}
                getDayClassName={getDayClassName}
                minDate={
                  showCalendar === 'from'
                    ? new Date(new Date().setHours(0,0,0,0))
                    : fromDate && fromDate instanceof Date
                      ? new Date(fromDate.setHours(0,0,0,0))
                      : undefined
                }
              />
            </div>
            
          </>
        )}
        {/* Tab 2: Instructor Availability */}
        {tab === 1 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 4,
                width: "100%",
                flexWrap: "nowrap",   // prevent wrapping in the header row
                minWidth: 0           // allow children to shrink
              }}
            >
             
              {copySourceDay && (
                <button
                  style={{
                    marginLeft: 8,
                    background: "#1976d2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    lineHeight: 1.1,
                    cursor: "pointer",
                    flex: "0 0 auto"       // don't let button force wrapping
                  }}
                  onClick={handleCopyDone}
                >
                  Done
                </button>
              )}
            </div>
            <div>
              {availability.map((day) => {
                const firstSlot = day.slots[0];
                const restSlots = day.slots.slice(1);
                return (
                  <React.Fragment key={day.day}>
                    {/* Row 1: Day | First slot OR Unavailable | + icon at end | Paste */}
                    <div className="admin-dashboard-day-row" style={{ alignItems: "flex-start" }}>
                      {/* Day cell */}
                      <div className="admin-dashboard-day-label" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <span>{day.day}</span>
                        {/* Icons in their own cell, under the day */}
                        <div
                          className="admin-dashboard-day-actions-vertical"
                          style={{ display: "flex", flexDirection: "row", gap: 8, marginTop: 4 }}
                        >
                          {day.slots.length > 0 && (
                            <>
                              <img
                                src={deleteIcon}
                                alt="Delete"
                                title="Remove all slots"
                                className="admin-dashboard-slot-action"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setAvailability(prev =>
                                    prev.map(d => (d.day === day.day ? { ...d, slots: [] } : d))
                                  );
                                }}
                              />
                              <img
                                src={copyIcon}
                                alt="Copy"
                                title="Copy slots"
                                className="admin-dashboard-slot-action"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleCopySlots(day.day)}
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {/* Slots cell: first slot or unavailable, and rest slots on same line */}
                      <div className="admin-dashboard-day-slots" style={{ display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, minHeight: 32 }}>
                        {firstSlot ? (
                          <div className="admin-dashboard-slot first-slot" style={{ display: "inline-flex", alignItems: "center" }}>
                            <span className="admin-dashboard-slot-time">{firstSlot.start}</span>
                            <span className="admin-dashboard-slot-separator">–</span>
                            <span className="admin-dashboard-slot-time">{firstSlot.end}</span>
                          </div>
                        ) : (
                          <div className="admin-dashboard-unavailable">
                            Unavailable
                          </div>
                        )}
                        {restSlots.length > 0 && restSlots.map((slot, i) => (
                          <div key={i} className="admin-dashboard-slot" style={{ display: "inline-flex", alignItems: "center" }}>
                            <span className="admin-dashboard-slot-time">{slot.start}</span>
                            <span className="admin-dashboard-slot-separator">–</span>
                            <span className="admin-dashboard-slot-time">{slot.end}</span>
                          </div>
                        ))}
                      </div>

                      {/* + icon at end of row */}
                      <div className="admin-dashboard-day-actions">
                        <span
                          className="admin-dashboard-add-btn"
                          title="Add"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDropdown(day.day)}
                        >
                          <b>＋</b>
                        </span>
                        {copySourceDay && copySourceDay !== day.day && !pastedDays.includes(day.day) && (
                          <button
                            className="paste-btn"
                            style={{
                              background: "#1976d2",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              lineHeight: 1.1,
                              cursor: "pointer",
                              marginLeft: 8
                            }}
                            onClick={() => handlePasteSlots(day.day)}
                          >
                            Paste
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Dropdown row (when adding) */}
                    {!copySourceDay && dropdowns[day.day]?.open && (
                      <div className="admin-dashboard-day-row sub" style={{ marginBottom: 8 }}>
                        <div className="admin-dashboard-day-label" />
                        <div className="admin-dashboard-day-content">
                          <div className="admin-dashboard-dropdown" style={{ justifyContent: "flex-start" }}>
                            <select
                              value={dropdowns[day.day]?.start || ''}
                              onChange={e => handleTimeChange(day.day, 'start', e.target.value)}
                            >
                              <option value="">Start</option>
                              {TIME_OPTIONS.map(time => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                            <span style={{ color: "#888" }}>–</span>
                            <select
                              value={dropdowns[day.day]?.end || ''}
                              onChange={e => handleTimeChange(day.day, 'end', e.target.value)}
                            >
                              <option value="">End</option>
                              {TIME_OPTIONS.map(time => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                            <button className="admin-dashboard-dropdown-btn" onClick={() => handleAddSlot(day.day)} type="button">
                              Add
                            </button>
                          </div>
                        </div>
                        <div className="admin-dashboard-day-actions" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            
          </>
        )}
        {/* Tab 3: Confirmation */}
        {tab === 2 && (
          <>
            
            <div style={{ textAlign: "center", margin: "5px 0" }}>
              <h2 style={{ color: "#282c34" }}>
                {isSchool ? "Confirm School Booking Schedule" : "Confirm Your Lesson Schedule"}
              </h2>
              <div style={{ margin: "18px 0" }}>
                <strong>Duration:</strong> {duration}<br />
                <strong>From:</strong> {formatDate(fromDate)}<br />
                <strong>To:</strong> {formatDate(toDate)}
              </div>
              <div style={{ margin: "18px 0" }}>
                <strong>{isSchool ? "School Availability" : "Instructor Availability"}:</strong>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {availability.map(day =>
                    day.slots.length > 0 ? (
                      <li key={day.day}>
                        <strong>{day.day}:</strong>{" "}
                        {day.slots.map((slot, i) => (
                          <span key={i}>
                            {slot.start}–{slot.end}
                            {i < day.slots.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>
            <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 24 }}>
              <button
                style={{
                  padding: "10px 28px",
                  borderRadius: "8px",
                  border: "none",
                  background: canConfirm ? "#282c34" : "#bbb",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "1rem",
                  cursor: canConfirm ? "pointer" : "not-allowed",
                  alignSelf: "center",
                  opacity: canConfirm ? 1 : 0.6
                }}
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LessonSchedule;